import type {
  EventListPageTypeString,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'
import { getEventListPageStrapiURL } from '~/utils/strapiURLhelpers'
import { dynamicContentTransformer } from '~/utils/dynamicContentTransformer'
import {
  convertISO2Locale,
  convertLocaleIntoBCP47,
} from '~/utils/localeHelpers'
import { useStrapiConfig, buildStrapiUrl } from '~/composables/useStrapi'

const FETCH_TIMEOUT_MS = 10_000

interface UseResolvedEventListPageParams {
  pageType: EventListPageTypeString
  slug?: string
  locationSlug?: string
  categorySlug?: string
  skipDynamicContent?: boolean
}

const PAGE_TYPE_TO_RESOURCE: Record<string, string> = {
  [EventListPageType.CITY]: 'Locality',
  [EventListPageType.CATEGORY]: 'Category',
  [EventListPageType.CITY_CATEGORY]: 'LocalityCategory',
  [EventListPageType.GENERAL]: 'Tag',
  [EventListPageType.TEAMEVENT]: 'Tag',
}

const getMetricsSlug = (
  pageType: EventListPageTypeString,
  params: Pick<
    UseResolvedEventListPageParams,
    'locationSlug' | 'categorySlug' | 'slug'
  >
): string | undefined => {
  const slugMap: Record<string, string | undefined> = {
    [EventListPageType.CITY]: params.locationSlug,
    [EventListPageType.CATEGORY]: params.categorySlug,
    [EventListPageType.CITY_CATEGORY]: `${params.locationSlug}-${params.categorySlug}`,
    [EventListPageType.GENERAL]: params.slug,
    [EventListPageType.TEAMEVENT]: params.slug,
  }
  return slugMap[pageType]
}

export const useResolvedEventListPage = (
  params: UseResolvedEventListPageParams
) => {
  const { locale, locales } = useI18n()
  const route = useRoute()
  const config = useRuntimeConfig()
  const strapiConfig = useStrapiConfig()

  const { apiBaseURL } = config.public
  const localeBCP47 = convertLocaleIntoBCP47(locale.value)
  const localeISO2 = convertISO2Locale(locale.value)
  const kftApiHeaders = {
    'Content-Type': 'application/json',
    'Accept-Language': `${localeBCP47},${localeISO2}`,
  }

  const fetchStrapiPages = async (
    localeCode: string
  ): Promise<StrapiEventListPage[]> => {
    const urlSuffix = getEventListPageStrapiURL(
      localeCode,
      params.slug,
      params.locationSlug,
      params.categorySlug
    )
    const url = buildStrapiUrl(strapiConfig, urlSuffix)

    try {
      const response = await $fetch<
        StrapiApiDefaultResponse<StrapiEventListPage[]>
      >(url, { headers: strapiConfig.headers, timeout: FETCH_TIMEOUT_MS })
      return response.data ?? []
    } catch {
      return []
    }
  }

  const fetchPagesWithLocaleFallback = async (): Promise<
    StrapiEventListPage[]
  > => {
    const pages = await fetchStrapiPages(locale.value)
    if (pages.length) return pages

    const allResults = await Promise.all(
      locales.value.map((l) => fetchStrapiPages(l.code))
    )
    return allResults.flat()
  }

  const fetchDynamicContent =
    async (): Promise<KftDynamicContentMetrics | null> => {
      const resource = PAGE_TYPE_TO_RESOURCE[params.pageType]
      const slug = getMetricsSlug(params.pageType, params)
      if (!resource || !slug) return null

      try {
        const response = await $fetch<
          KftApiDefaultResponse<KftDynamicContentMetrics>
        >(`/v1/store/metrics/${resource}/${slug}`, {
          baseURL: apiBaseURL as string,
          headers: kftApiHeaders,
          timeout: FETCH_TIMEOUT_MS,
        })
        return response?.data ?? null
      } catch {
        return null
      }
    }

  const redirectIfLocaleSlugMismatch = async (
    firstEntry: StrapiEventListPage
  ): Promise<boolean> => {
    const firstEntryLocale = strapiEventListPageGetters
      .getLocale(firstEntry)
      ?.toLowerCase()
    if (firstEntryLocale === locale.value) return false

    const localization = strapiEventListPageGetters.getLocalizationByLocale(
      firstEntry,
      locale.value
    )
    if (!localization) return false

    const entryLocale = strapiEventListPageGetters
      .getLocale(localization)
      ?.toLowerCase()
    const pagePath =
      strapiEventListPageGetters.getKonfettiPagePath(localization)
    const queryParams = route.fullPath.split('?')[1]
    let correctUrl = `/${entryLocale}${pagePath}/`

    if (queryParams && !correctUrl.includes('?')) {
      correctUrl += `?${queryParams}`
    }

    if (route.fullPath !== correctUrl) {
      await navigateTo({ path: correctUrl }, { redirectCode: 301 })
      return true
    }

    return false
  }

  const resolveLocaleEntry = (
    pages: StrapiEventListPage[]
  ): StrapiEventListPage | null => {
    return (
      pages.find((e) => e?.locale.toLowerCase() === locale.value) ??
      pages[0] ??
      null
    )
  }

  const applyDynamicContent = async (
    entry: StrapiEventListPage
  ): Promise<StrapiEventListPage> => {
    if (params.skipDynamicContent) return entry

    const metrics = await fetchDynamicContent()
    if (!metrics) return entry

    return dynamicContentTransformer(
      entry,
      metrics,
      locale.value
    ) as StrapiEventListPage
  }

  const cacheKey = `resolved-elp-${params.pageType}-${params.slug}-${params.locationSlug}-${params.categorySlug}`

  return useAsyncData<
    StrapiEventListPage | null,
    Error,
    StrapiEventListPage | null
  >(
    cacheKey,
    async (): Promise<StrapiEventListPage | null> => {
      const pages = await fetchPagesWithLocaleFallback()
      if (!pages.length) return null

      const redirected = await redirectIfLocaleSlugMismatch(pages[0]!)
      if (redirected) return null

      const entry = resolveLocaleEntry(pages)
      if (!entry) return null

      return applyDynamicContent(entry)
    },
    {
      server: true,
      lazy: false,
      dedupe: 'defer',
    }
  )
}
